import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import apiRequest from "../../../utils/vendorManager/apiRequest";
import InputField from "../../ui/form/InputField";
import FileInputField from "../../ui/form/FileInputField";
import SelectField from "../../ui/form/SelectField";
import CheckboxField from "../../ui/form/CheckboxField";
import Button from "../../ui/utils/Button";
import usePost from "../../../hooks/api/usePost";
import Alert from "../../ui/utils/Alert";

const CreateSetting = ({ closeModal, callback }) => {
  const [preview, setPreview] = useState(null);

  const {
    error,
    isLoading: loading,
    post,
    serverErrors,
  } = usePost(apiRequest.createVendorSetting);

  const formik = useFormik({
    initialValues: {
      type: "",
      value: "",
      image: null,
      removeBg: false,
      is_vat: false,
      is_cgst: false,
      is_sgst: false,
    },

    validationSchema: Yup.object({
      type: Yup.string()
        .oneOf(["logo", "slider", "text",'vat','cgst','sgst'])
        .required("Type is required"),

      value: Yup.string().when("type", {
        is: "text",
        then: (schema) => schema.required("Value is required"),
        otherwise: (schema) => schema.nullable(),
      }),

      image: Yup.mixed().when("type", {
        is: (val) => val === "logo" || val === "slider",
        then: (schema) =>
          schema
            .required("Image is required")
            .test("fileType", "Invalid file type", (value) =>
              value
                ? ["image/jpeg", "image/png", "image/webp"].includes(value.type)
                : false
            ),
        otherwise: (schema) => schema.nullable(),
      }),
    }),

    onSubmit: async (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("type", values.type);

      if (values.type === "text") {
        formData.append("value", values.value);
      }

      if (values.type === "logo" || values.type === "slider") {
        formData.append("image", values.image);
        formData.append("removeBg", values.removeBg.toString());
      }

      const response = await post({ data: { formData } });

      if (response?.response) {
        callback?.();
        resetForm();
        closeModal();
      }
    },
  });

  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">
        Create Vendor Site Setting
      </h2>
      <div className="border-b" />

      <form onSubmit={formik.handleSubmit} className="text-sm">
        {error && <Alert message={error} type="error" />}

        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SelectField
            name="type"
            label="Setting Type"
            value={formik.values.type}
            onChange={(e) => {
              formik.handleChange(e);
              setPreview(null);
              formik.setFieldValue("image", null);
              formik.setFieldValue("value", "");

              formik.setFieldValue("is_vat", false);
              formik.setFieldValue("is_cgst", false);
              formik.setFieldValue("is_sgst", false);
            }}
            options={[
              { label: "Select Type", value: "" },
              { label: "Logo", value: "logo" },
              { label: "Slider Image", value: "slider" },
              { label: "Text", value: "text" },
              { label: "VAT", value: "vat" },
              { label: "CGST", value: "cgst" },
              { label: "SGST", value: "sgst" },
            ]}
            error={formik.touched.type && formik.errors.type}
            serverError={serverErrors?.type}
          />

          {formik.values.type === "text" && (
            <InputField
              name="value"
              label="Text Value"
              value={formik.values.value}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.value && formik.errors.value}
              serverError={serverErrors?.value}
              placeholder="Enter text value"
            />
          )}

          {(formik.values.type === "logo" ||
            formik.values.type === "slider") && (
              <div className="col-span-full">
                <FileInputField
                  id="image"
                  label="Image"
                  accept="image/*"
                  error={formik.touched.image && formik.errors.image}
                  onChange={(e) => {
                    const file = e.currentTarget.files?.[0] || null;
                    formik.setFieldValue("image", file);
                    setPreview(file ? URL.createObjectURL(file) : null);
                  }}
                />

                {preview && (
                  <img
                    src={preview}
                    alt="Preview"
                    className="w-20 h-20 mt-2 border rounded object-contain"
                  />
                )}

                <CheckboxField
                  checked={formik.values.removeBg}
                  name="removeBg"
                  label="Remove Background"
                  onChange={formik.handleChange}
                />
              </div>
            )}

          {/* TAX CHECKBOXES */}
          {formik.values.type === "vat" && (
            <div className="col-span-full">
              <CheckboxField
                id="is_vat"
                label="Enable VAT"
                checked={formik.values.is_vat}
                onChange={(e) =>
                  formik.setFieldValue("is_vat", e.target.checked)
                }
              />
            </div>
          )}

          {formik.values.type === "cgst" && (
            <div className="col-span-full">
              <CheckboxField
                id="is_cgst"
                label="Enable CGST"
                checked={formik.values.is_cgst}
                onChange={(e) =>
                  formik.setFieldValue("is_cgst", e.target.checked)
                }
              />
            </div>
          )}

          {formik.values.type === "sgst" && (
            <div className="col-span-full">
              <CheckboxField
                id="is_sgst"
                label="Enable SGST"
                checked={formik.values.is_sgst}
                onChange={(e) =>
                  formik.setFieldValue("is_sgst", e.target.checked)
                }
              />
            </div>
          )}


        </div>

        <div className="border-b" />
        <div className="flex justify-end gap-2 p-4">
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loadingText="Creating..."
            isLoading={loading}
          >
            Create
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateSetting;
